/**
 * EVENTSFLO-CREW-PWA-BUILD-VERSION-AND-AUTO-UPDATE-V1
 *
 * Service-worker registration + update coordination for the Crew PWA.
 *
 * - Registers /sw.js on app boot.
 * - Logs the current build to console on every PWA load.
 * - Detects a waiting worker ("update available"), tells it to skipWaiting,
 *   then triggers ONE reload via `controllerchange` using a session-scoped
 *   guard so we never get stuck in a reload loop.
 * - Exposes a `checkForUpdates()` helper that the Settings / Profile pages
 *   can call from a "Check for updates" button.
 * - Exposes `getSwStatus()` to drive the diagnostic line.
 *
 * Works fine when service workers are unsupported — all helpers return a
 * graceful "unsupported" state and never throw.
 */
import {
  APP_VERSION,
  BUILD_VERSION,
  BUILD_TIMESTAMP,
  BUILD_DATE,
  BUILD_COMMIT,
} from "./version";

const RELOAD_GUARD_KEY = "icrew:sw-reloaded-for";
const LAST_CHECK_KEY = "icrew:sw-last-update-check";

let _registration = null;
let _updateAvailable = false;
const _listeners = new Set();

function notify() {
  const status = getSwStatus();
  _listeners.forEach((fn) => {
    try {
      fn(status);
    } catch (err) {
      // Never let a subscriber break the whole pipeline.
      console.warn("[iCrew SW] listener error:", err);
    }
  });
}

function markLastCheck() {
  try {
    localStorage.setItem(LAST_CHECK_KEY, new Date().toISOString());
  } catch {
    /* ignore (private mode / quota) */
  }
}

function reloadOnce(reason) {
  // Guard against reload loops: only reload once per BUILD_VERSION per tab.
  let alreadyReloaded;
  try {
    alreadyReloaded = sessionStorage.getItem(RELOAD_GUARD_KEY) === BUILD_VERSION;
  } catch {
    alreadyReloaded = false;
  }
  if (alreadyReloaded) {
    console.warn(`[iCrew SW] Skip reload (already reloaded this session for ${BUILD_VERSION}). reason=${reason}`);
    return;
  }
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, BUILD_VERSION);
  } catch {
    /* ignore */
  }
  console.log(`[iCrew SW] Reloading window for new build. reason=${reason}`);
  window.location.reload();
}

function attachRegistrationHandlers(registration) {
  _registration = registration;

  // If a SW is already installed and waiting → update available.
  // EVENTSFLO-FRONTEND-UPDATE-AVAILABLE-REFRESH-TOAST-V1: previously this
  // also auto-fired `skipWaiting` so the page reloaded silently. We now
  // expose the update-available signal to listeners (the toast) and let
  // the user explicitly opt in via `activateWaitingAndReload()`.
  if (registration.waiting && navigator.serviceWorker.controller) {
    _updateAvailable = true;
    notify();
  }

  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;
    if (!installing) return;
    console.log("[iCrew SW] updatefound — new worker installing…");
    installing.addEventListener("statechange", () => {
      if (installing.state === "installed") {
        if (navigator.serviceWorker.controller) {
          // A previous SW was controlling the page — this is an update,
          // not the first install. Surface the toast; don't auto-reload.
          _updateAvailable = true;
          notify();
        } else {
          console.log("[iCrew SW] First-time install complete.");
        }
      }
    });
  });
}

/**
 * EVENTSFLO-FRONTEND-UPDATE-AVAILABLE-REFRESH-TOAST-V1
 * User-initiated activation of the waiting service worker, followed by a
 * single page reload via the existing `controllerchange` listener.
 *
 * Safe to call at any time. Returns one of:
 *  - { status: "activating" }     waiting worker told to skipWaiting
 *  - { status: "no-waiting" }     no waiting worker — fall back to hard reload
 *  - { status: "unsupported" }    serviceWorker API absent — hard reload
 *
 * In the "no-waiting" / "unsupported" branches we call reloadOnce() so the
 * user always gets a fresh bundle when they click "Refresh now".
 */
export function activateWaitingAndReload() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    reloadOnce("user-refresh-unsupported");
    return { status: "unsupported" };
  }
  const reg = _registration;
  if (reg && reg.waiting) {
    try {
      reg.waiting.postMessage("skipWaiting");
    } catch (err) {
      console.warn("[iCrew SW] skipWaiting postMessage failed:", err);
    }
    // controllerchange will fire and reloadOnce() will run.
    return { status: "activating" };
  }
  reloadOnce("user-refresh-no-waiting");
  return { status: "no-waiting" };
}

/**
 * Register the service worker. Call once at app boot.
 */
export function registerSw() {
  // Log the running build every time the PWA loads (helps mobile debugging).
  // eslint-disable-next-line no-console
  console.info(
    `[iCrew] App boot — version=${APP_VERSION} build=${BUILD_VERSION} ts=${BUILD_TIMESTAMP} date=${BUILD_DATE}${BUILD_COMMIT ? ` commit=${BUILD_COMMIT}` : ""}`
  );

  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  // One-time controllerchange listener → reload when the new SW takes over.
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    reloadOnce("controllerchange");
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((registration) => {
        console.log("[iCrew SW] Registered:", registration.scope);
        attachRegistrationHandlers(registration);
        // Proactive update check on boot (fire-and-forget).
        registration.update().catch(() => {});
        markLastCheck();
        notify();
      })
      .catch((err) => {
        console.warn("[iCrew SW] Registration failed:", err);
      });
  });
}

/**
 * Manually trigger a service-worker update check.
 *
 * Strategy:
 *  1. Fetch /version.json with `cache: "no-store"` so the comparison is based
 *     on the freshly deployed metadata, not a stale cached copy.
 *  2. Call registration.update() to give the browser a chance to install a
 *     new /sw.js if its bytes changed.
 *  3. If a waiting worker exists, postMessage("skipWaiting") + clear caches
 *     and let `controllerchange` reload the page exactly once.
 *  4. If `/version.json` reports a newer build but no SW transitioned, fall
 *     back to clearing caches + a hard reload so the user still gets new code.
 *  5. Return one of:
 *     - { status: "update-activating" }   (waiting SW now skipWaiting → reload)
 *     - { status: "reloaded" }            (forced hard reload happened)
 *     - { status: "latest" }              (already on the latest deployed build)
 *     - { status: "unable", error }       (network/SW errors)
 *     - { status: "unsupported" }         (no SW support — caller does fallback)
 */
export async function checkForUpdates({ clearCaches = false } = {}) {
  if (typeof window === "undefined") {
    return { status: "unsupported" };
  }

  // Always probe /version.json fresh so the diagnostic line + comparison
  // never lie about what is actually deployed.
  let serverVersion = null;
  try {
    const res = await fetch("/version.json", {
      cache: "no-store",
      credentials: "omit",
      headers: { "Cache-Control": "no-cache" },
    });
    if (res.ok) {
      const json = await res.json();
      serverVersion = json?.build_version || json?.build_timestamp || null;
    }
  } catch (err) {
    console.warn("[iCrew SW] /version.json fetch failed:", err);
  }
  const installedVersion = `v${BUILD_TIMESTAMP}`;
  const remoteHasNewer =
    serverVersion && serverVersion !== installedVersion;

  if (!("serviceWorker" in navigator)) {
    markLastCheck();
    notify();
    if (remoteHasNewer) {
      reloadOnce("unsupported-but-newer-server-build");
      return { status: "reloaded" };
    }
    return { status: "unsupported", serverVersion, installedVersion };
  }

  const registration =
    _registration || (await navigator.serviceWorker.getRegistration("/"));
  markLastCheck();

  if (!registration) {
    notify();
    reloadOnce("no-registration");
    return { status: "no-registration" };
  }

  try {
    await registration.update();
  } catch (err) {
    console.warn("[iCrew SW] update() failed:", err);
    return { status: "unable", error: String(err) };
  }

  if (clearCaches && "caches" in window) {
    try {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
      console.log("[iCrew SW] Cleared caches:", names);
    } catch (err) {
      console.warn("[iCrew SW] Cache clear failed:", err);
    }
  }

  if (registration.waiting) {
    _updateAvailable = true;
    notify();
    try {
      registration.waiting.postMessage("skipWaiting");
    } catch {
      /* ignore */
    }
    return { status: "update-activating", serverVersion, installedVersion };
  }

  notify();

  // The SW didn't transition to "waiting" but version.json says the deploy
  // is newer — force a reload so the JS bundle is re-fetched.
  if (remoteHasNewer) {
    reloadOnce("server-build-newer-no-waiting-sw");
    return { status: "reloaded", serverVersion, installedVersion };
  }

  if (clearCaches) {
    reloadOnce("manual-refresh-no-update");
    return { status: "reloaded" };
  }

  return { status: "latest", serverVersion, installedVersion };
}

/**
 * Returns the current SW status for UI display.
 */
export function getSwStatus() {
  const supported =
    typeof window !== "undefined" && "serviceWorker" in navigator;
  if (!supported) {
    return {
      supported: false,
      controlled: false,
      updateAvailable: false,
      lastCheck: null,
      version: BUILD_VERSION,
      buildTimestamp: BUILD_TIMESTAMP,
      buildCommit: BUILD_COMMIT,
    };
  }
  let lastCheck = null;
  try {
    lastCheck = localStorage.getItem(LAST_CHECK_KEY);
  } catch {
    /* ignore */
  }
  return {
    supported: true,
    controlled: Boolean(navigator.serviceWorker.controller),
    updateAvailable: _updateAvailable,
    lastCheck,
    version: BUILD_VERSION,
    buildTimestamp: BUILD_TIMESTAMP,
    buildCommit: BUILD_COMMIT,
  };
}

/**
 * Subscribe to SW status changes — used by the diagnostic panel.
 * Returns an unsubscribe function.
 */
export function subscribeSwStatus(listener) {
  _listeners.add(listener);
  // Push the current state immediately so the UI starts in sync.
  try {
    listener(getSwStatus());
  } catch {
    /* ignore */
  }
  return () => _listeners.delete(listener);
}

/**
 * EVENTSFLO-FRONTEND-UPDATE-AVAILABLE-REFRESH-TOAST-V1
 *
 * Manually flag an update as available and fire all listeners. Used by
 * QA/headless tests to verify the "Update available — refresh" toast
 * without having to coordinate a real Service Worker upgrade cycle.
 *
 * Exposed on `window.__icrewSimulateSwUpdate` for in-browser triggering.
 * Safe in production: it only flips a UI flag and lets the user opt in
 * to a normal `window.location.reload()`. No code execution, no privileged
 * action. Useful when an admin reports "I think a deploy happened" and
 * the SW lifecycle hasn't reported it yet.
 */
export function simulateSwUpdateAvailable() {
  _updateAvailable = true;
  notify();
}

if (typeof window !== "undefined") {
  window.__icrewSimulateSwUpdate = simulateSwUpdateAvailable;
}
